import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Badge } from '@vaiu/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { 
  MessageCircle, 
  Send, 
  Users, 
  Store, 
  Gift, 
  Zap,
  Bot,
  User,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Mail,
  ArrowRight,
  Sparkles,
  Heart,
  Star,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Shield,
  Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { User as UserModel } from '@/models/User';

interface Message {
  id: string;
  text: string;
  type: 'bot' | 'user' | 'system';
  timestamp: Date;
  options?: {
    text: string;
    action: string;
    value?: string;
    icon?: React.ReactNode;
  }[];
  component?: React.ReactNode;
}

interface UserInterest {
  type: 'merchant' | 'advocate' | 'rewards' | 'community';
  level: 'high' | 'medium' | 'low';
  reasons?: string[];
}

const InteractiveOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { user, isGuest, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [currentStep, setCurrentStep] = useState('greeting');
  const [userInterests, setUserInterests] = useState<UserInterest[]>([]);
  const [userName, setUserName] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [reminderEmail, setReminderEmail] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const greetingInitialized = useRef(false);
  const firstInteractionLogged = useRef(false);
  const [sessionId] = useState(() => `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Network stats
  const stats = {
    merchants: 1247,
    advocates: 8934,
    requests: 15678,
    rewards: 1256
  };

  // Log conversation data for admin review (only for guests and unauthenticated users)
  // Must be defined before useEffect hooks that use it
  const logConversationData = async (interactionType: string, userMessage?: string, botResponse?: string, userChoice?: string) => {
    try {
      const logData = {
        sessionId,
        timestamp: new Date().toISOString(),
        interactionType,
        userMessage,
        botResponse,
        userChoice,
        currentStep,
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
        userType: !user ? 'unauthenticated' : 'guest'
      };
      
      // Try to send to backend API first (if endpoint exists)
      // Note: This endpoint may not exist yet in the backend
      // Silently fall back to localStorage if it doesn't
      try {
        const response = await fetch('/api/v1/admin/guest-conversations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || 'guest'}`
          },
          body: JSON.stringify(logData)
        });
        
        if (response.ok) {
          console.log('✅ Guest conversation logged to backend');
          return; // Success - no need to store locally
        } else if (response.status === 404 || response.status === 405) {
          // Expected - endpoint doesn't exist yet, silently use localStorage
          // Don't log as warning since this is expected behavior
        } else {
          // Unexpected error - log it
          console.warn('⚠️ Backend logging failed with status:', response.status);
        }
      } catch (apiError) {
        // Network error - silently fall back to localStorage
        // This is expected in development or when backend is down
      }
      
      // Fallback to localStorage if backend fails
      const existingLogs = JSON.parse(localStorage.getItem('guestConversationLogs') || '[]');
      existingLogs.push(logData);
      
      // Keep only last 100 interactions to prevent storage overflow
      if (existingLogs.length > 100) {
        existingLogs.splice(0, existingLogs.length - 100);
      }
      
      localStorage.setItem('guestConversationLogs', JSON.stringify(existingLogs));
      
    } catch (error) {
      console.error('Error logging conversation data:', error);
    }
  };

  /*
   * ADMIN REVIEW DATA STRUCTURE:
   * Each logged interaction contains:
   * - sessionId: Unique session identifier
   * - timestamp: When the interaction occurred
   * - interactionType: 'session_start', 'user_message', 'user_choice', or 'bot_response'
   * - userMessage: Free-form text input from user
   * - botResponse: Bot's response text
   * - userChoice: Button/option selected by user
   * - currentStep: Current conversation step/state
   * - userAgent: Browser/device information
   * - referrer: How user arrived at the page
   * 
   * BACKEND API ACCESS:
   * - POST /api/v1/admin/guest-conversations (log interaction)
   * - GET /api/v1/admin/guest-conversations (retrieve logs)
   * - Requires ADMIN, CUSTOMER_SUPPORT, or PRODUCT_ADMIN role
   * 
   * FALLBACK ACCESS:
   * - localStorage: JSON.parse(localStorage.getItem('guestConversationLogs'))
   * - Only used when backend API is unavailable
   */

  // All function definitions must be before useEffect hooks that use them
  const scrollToBottom = () => {
    // Only scroll within the chat container, not the entire page
    if (messagesEndRef.current && messagesEndRef.current.parentElement) {
      // Use scrollTop on the container instead of scrollIntoView to avoid page scroll
      const container = messagesEndRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  };

  const addBotMessage = (text: string, options?: Message['options'], component?: React.ReactNode) => {
    setIsTyping(true);
    setTimeout(() => {
      const newMessage: Message = {
        id: Date.now().toString(),
        text,
        type: 'bot',
        timestamp: new Date(),
        options,
        component
      };
      setMessages(prev => [...prev, newMessage]);
      setIsTyping(false);
      
      // Only log bot response if user has interacted (not for initial greeting)
      // This prevents logging when users are just browsing without engaging
      if (firstInteractionLogged.current) {
        logConversationData('bot_response', undefined, text);
      }
    }, 1000 + Math.random() * 1000); // Simulate typing delay
  };

  const addUserMessage = (text: string) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      type: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
    
    // Log user message for admin review
    logConversationData('user_message', text);
  };

  const handleOptionClick = (action: string, value?: string, optionText?: string) => {
    // Log session start on first interaction (not on component mount)
    if (!firstInteractionLogged.current) {
      firstInteractionLogged.current = true;
      logConversationData('session_start');
    }
    
    // Log user choice for admin review
    logConversationData('user_choice', undefined, undefined, optionText || action);
    
    addUserMessage(optionText || action);
    
    setTimeout(() => {
      switch (action) {
        case 'rewards_interest':
          setCurrentStep('rewards');
          addBotMessage(
            `That's awesome! 🎉 With Vaiu, you can earn rewards just by requesting your favorite merchants join our network. We've already paid out $${stats.rewards.toLocaleString()} to ${stats.advocates.toLocaleString()} advocates like you!`,
            [
              { text: "How does it work?", action: 'rewards_how', icon: <Zap className="w-4 h-4" /> },
              { text: "What kind of rewards?", action: 'rewards_types', icon: <Gift className="w-4 h-4" /> },
              { text: "Sign me up!", action: 'signup', icon: <ArrowRight className="w-4 h-4" /> }
            ]
          );
          break;

        case 'merchant_discovery':
          setCurrentStep('discovery');
          //            `Perfect! 🛍️ We have ${stats.merchants.toLocaleString()} amazing merchants already on Vaiu Network. Plus, you can request new ones and earn rewards when they join!`,

          addBotMessage(
            `Perfect! 🛍️ There are ${stats.merchants.toLocaleString()} amazing merchants on the network. You can help new ones sign up and you will earn rewards when they join!`,
            [
              { text: "Show me the merchants", action: 'browse_merchants', icon: <Store className="w-4 h-4" /> },
              { text: "How do I request merchants?", action: 'request_how', icon: <Users className="w-4 h-4" /> },
              { text: "What are the benefits?", action: 'benefits', icon: <Star className="w-4 h-4" /> }
            ]
          );
          break;

        case 'merchant_interest':
          setCurrentStep('merchant');
          addBotMessage(
            `Fantastic! 🏪 As a business owner, you can join our growing network of ${stats.merchants.toLocaleString()} merchants and tap into our community of ${stats.advocates.toLocaleString()} advocates who actively promote businesses like yours.`,
            [
              { text: "What's in it for my business?", action: 'merchant_benefits', icon: <TrendingUp className="w-4 h-4" /> },
              { text: "How do I get started?", action: 'merchant_signup', icon: <ArrowRight className="w-4 h-4" /> },
              { text: "Tell me about the advocates", action: 'advocate_info', icon: <Users className="w-4 h-4" /> }
            ]
          );
          break;

        case 'privacy_motivated':
          setCurrentStep('privacy');
          addBotMessage(
            `I totally understand! 🔒 Privacy is super important. With Vaiu, you control your data and choose how much you share. You can get paid without giving away your phone number to random apps or merchants.`,
            [
              { text: "How does Vaiu protect my privacy?", action: 'privacy_protection', icon: <Shield className="w-4 h-4" /> },
              { text: "What data do you actually need?", action: 'data_minimal', icon: <Users className="w-4 h-4" /> },
              { text: "Show me the privacy-first approach", action: 'privacy_approach', icon: <ArrowRight className="w-4 h-4" /> }
            ]
          );
          break;

        case 'tech_motivated':
          setCurrentStep('tech');
          addBotMessage(
            `Yes! 🚀 You get it! We're building the future of payments using cutting-edge tech. Think decentralized networks, AI-powered matching, and blockchain-level security - but with the simplicity your grandma could use.`,
            [
              { text: "Tell me about the technology", action: 'tech_deep_dive', icon: <Zap className="w-4 h-4" /> },
              { text: "How is it more equitable?", action: 'equity_focus', icon: <Heart className="w-4 h-4" /> },
              { text: "What makes it faster/safer?", action: 'tech_benefits', icon: <Shield className="w-4 h-4" /> }
            ]
          );
          break;

        case 'browsing':
          setCurrentStep('browsing');
          addBotMessage(
            `No problem! 😊 Let me quickly show you what makes Vaiu special. We're not just another payment app - we're building a community-driven network where everyone wins.`,
            [
              { text: "What makes it different?", action: 'difference', icon: <Sparkles className="w-4 h-4" /> },
              { text: "Show me the numbers", action: 'stats', icon: <TrendingUp className="w-4 h-4" /> },
              { text: "Maybe remind me later", action: 'reminder', icon: <Clock className="w-4 h-4" /> }
            ]
          );
          break;

        case 'rewards_how':
          addBotMessage(
            `It's super simple! 🎯\n\n1. Request your favorite merchants to join Vaiu\n2. We reach out to them on your behalf\n3. When they sign up, you get rewarded!\n\nYou're basically growing a payment network you actually want to use.`,
            [
              { text: "That sounds great!", action: 'signup', icon: <ThumbsUp className="w-4 h-4" /> },
              { text: "What if they don't join?", action: 'no_join', icon: <MessageCircle className="w-4 h-4" /> },
              { text: "How much can I earn?", action: 'earn_amount', icon: <DollarSign className="w-4 h-4" /> }
            ]
          );
          break;

        case 'rewards_types':
          addBotMessage(
            `We offer multiple reward types! 💎\n\n• **Cash rewards** - Real money in your account\n• **Merchant credits** - Discounts at participating businesses\n• **Exclusive perks** - Early access to new features\n• **Community recognition** - Top advocate badges\n\nThe more you help grow the network, the more you earn!`,
            [
              { text: "I want to start earning!", action: 'signup', icon: <Zap className="w-4 h-4" /> },
              { text: "Tell me about merchant credits", action: 'merchant_credits', icon: <Gift className="w-4 h-4" /> }
            ]
          );
          break;

        case 'difference':
          addBotMessage(
            `Here's what sets us apart! ✨\n\n• **You help build it** - Request merchants YOU want\n• **Everyone gets rewarded** - You, the community, and businesses\n• **Community-driven** - Real people advocating for real businesses\n• **Transparent growth** - See exactly how your voice shapes the network\n\nIt's not about what WE want - it's about what YOU want!`,
            [
              { text: "I love this concept!", action: 'signup', icon: <Heart className="w-4 h-4" /> },
              { text: "Show me proof it works", action: 'stats', icon: <TrendingUp className="w-4 h-4" /> },
              { text: "Still not sure...", action: 'concerns', icon: <MessageCircle className="w-4 h-4" /> }
            ]
          );
          break;

        case 'stats':
          addBotMessage(
            `The numbers speak for themselves! 📊`,
            [],
            <StatsComponent stats={stats} />
          );
          setTimeout(() => {
            addBotMessage(
              "Pretty impressive, right? And this is just the beginning! Want to be part of this growing success story?",
              [
                { text: "Yes, count me in!", action: 'signup', icon: <ArrowRight className="w-4 h-4" /> },
                { text: "How can I contribute?", action: 'contribute', icon: <Users className="w-4 h-4" /> },
                { text: "Maybe later", action: 'reminder', icon: <Clock className="w-4 h-4" /> }
              ]
            );
          }, 2000);
          break;

        case 'concerns':
          addBotMessage(
            `I totally get it! 🤔 What's holding you back? I'd love to address any concerns you have.`,
            [
              { text: "Is it actually free?", action: 'free_concern', icon: <DollarSign className="w-4 h-4" /> },
              { text: "Will my data be safe?", action: 'privacy_concern', icon: <Shield className="w-4 h-4" /> },
              { text: "Do I need to buy anything?", action: 'purchase_concern', icon: <ShoppingBag className="w-4 h-4" /> },
              { text: "It seems too good to be true", action: 'skeptical', icon: <MessageCircle className="w-4 h-4" /> }
            ]
          );
          break;

        case 'free_concern':
          addBotMessage(
            `Absolutely 100% free! 🎉 We make money when merchants succeed, so we're aligned with your interests. No hidden fees, no subscriptions, no catch. You just advocate for businesses you love and get rewarded for it.`,
            [
              { text: "That's reassuring!", action: 'signup', icon: <ThumbsUp className="w-4 h-4" /> },
              { text: "How do YOU make money then?", action: 'business_model', icon: <MessageCircle className="w-4 h-4" /> }
            ]
          );
          break;

        case 'privacy_protection':
          addBotMessage(
            `Great question! 🛡️ Vaiu uses advanced encryption and gives you full control. We don't sell your data, we don't spam you, and you can delete your account anytime. Your privacy settings are granular - share only what you want, when you want.`,
            [
              { text: "That sounds refreshing!", action: 'signup', icon: <ArrowRight className="w-4 h-4" /> },
              { text: "Tell me more about data control", action: 'data_control', icon: <Users className="w-4 h-4" /> },
              { text: "How is this different from other apps?", action: 'privacy_comparison', icon: <Shield className="w-4 h-4" /> }
            ]
          );
          break;

        case 'data_minimal':
          addBotMessage(
            `Honestly? Just the basics to make payments work. 📱 Email for account recovery, your preferred payment method, and location (if you want local merchant suggestions). No invasive tracking, no selling your habits to advertisers.`,
            [
              { text: "Finally, an honest app!", action: 'signup', icon: <Sparkles className="w-4 h-4" /> },
              { text: "What about my payment info?", action: 'payment_security', icon: <Shield className="w-4 h-4" /> },
              { text: "Can I opt out of location sharing?", action: 'location_privacy', icon: <Users className="w-4 h-4" /> }
            ]
          );
          break;

        case 'privacy_approach':
          addBotMessage(
            `Privacy-first means we built the entire system around YOUR control. 🔐 Think of it like having your own personal vault - you decide who gets keys, when they expire, and what's inside. No surprise data harvesting!`,
            [
              { text: "I love this approach!", action: 'signup', icon: <Heart className="w-4 h-4" /> },
              { text: "Show me the privacy settings", action: 'privacy_demo', icon: <ArrowRight className="w-4 h-4" /> },
              { text: "What if I want to delete everything?", action: 'data_deletion', icon: <Shield className="w-4 h-4" /> }
            ]
          );
          break;

        case 'tech_deep_dive':
          addBotMessage(
            `Buckle up! 🚀 We're using distributed ledger technology for transaction integrity, AI for fraud detection and merchant matching, and progressive web apps for cross-platform compatibility. Plus, we're building towards Web3 interoperability.`,
            [
              { text: "Now we're talking!", action: 'signup', icon: <Zap className="w-4 h-4" /> },
              { text: "Tell me about the AI matching", action: 'ai_matching', icon: <Bot className="w-4 h-4" /> },
              { text: "How does Web3 fit in?", action: 'web3_integration', icon: <Globe className="w-4 h-4" /> }
            ]
          );
          break;

        case 'equity_focus':
          addBotMessage(
            `This is huge! 💪 We're leveling the playing field by giving small merchants the same payment capabilities as big corporations. No more gatekeepers, no discriminatory fees based on size. Everyone gets the same powerful tools.`,
            [
              { text: "That's exactly what we need!", action: 'signup', icon: <Heart className="w-4 h-4" /> },
              { text: "How do you help small businesses?", action: 'small_business_support', icon: <Store className="w-4 h-4" /> },
              { text: "What about international payments?", action: 'global_equity', icon: <Globe className="w-4 h-4" /> }
            ]
          );
          break;

        case 'tech_benefits':
          addBotMessage(
            `Speed: Real-time settlements, no waiting days. Safety: Military-grade encryption + biometric auth. Smart: AI prevents fraud before it happens. 🎯 All while being so simple, it feels like magic.`,
            [
              { text: "Sign me up for the future!", action: 'signup', icon: <Sparkles className="w-4 h-4" /> },
              { text: "Tell me about biometric auth", action: 'biometric_info', icon: <Shield className="w-4 h-4" /> },
              { text: "How fast are settlements?", action: 'settlement_speed', icon: <Zap className="w-4 h-4" /> }
            ]
          );
          break;

        case 'reminder':
          setCurrentStep('reminder');
          addBotMessage(
            `No worries at all! 😊 Life gets busy. Want me to send you a friendly reminder in a few days? Just drop your email and I'll check in with you.`,
            [
              { text: "Sure, remind me in a week", action: 'set_reminder', value: '7', icon: <Mail className="w-4 h-4" /> },
              { text: "Maybe in a month", action: 'set_reminder', value: '30', icon: <Mail className="w-4 h-4" /> },
              { text: "I'll bookmark this page", action: 'bookmark', icon: <Star className="w-4 h-4" /> }
            ]
          );
          break;

        case 'set_reminder':
          setCurrentStep('email_capture');
          const days = value === '7' ? 'a week' : 'a month';
          addBotMessage(
            `Perfect! I'll reach out in ${days}. What's your email address?`,
            [],
            <EmailCapture onSubmit={handleReminderSubmit} />
          );
          break;

        case 'signup':
          addBotMessage(
            `Awesome! 🎉 I love your enthusiasm! Let's get you set up so you can start earning rewards and building the payment network you want.`,
            [
              { text: "Take me to sign up!", action: 'navigate_signup', icon: <ArrowRight className="w-4 h-4" /> },
              { text: "Quick question first", action: 'pre_signup_question', icon: <MessageCircle className="w-4 h-4" /> }
            ]
          );
          break;

        case 'navigate_signup':
          navigate('/signup');
          break;

        case 'goodbye':
          addBotMessage(
            `Thanks for chatting with me! 😊 It was wonderful getting to know you. If you ever change your mind, we'll be here building the payment network of the future. Have a fantastic day! 👋✨`,
            []
          );
          break;

        default:
          addBotMessage("Wonderful! Sign up, participate, give and get, pay and get paid with confidence", [
            { text: "Sign up to learn more", action: 'navigate_signup', icon: <ArrowRight className="w-4 h-4" /> },
            { text: "Set a reminder", action: 'reminder', icon: <Clock className="w-4 h-4" /> }
          ]);
      }
    }, 500);
  };

  const handleReminderSubmit = (email: string) => {
    setReminderEmail(email);
    addUserMessage(email);
    setTimeout(() => {
      addBotMessage(
        `Got it! ✅ I'll send you a friendly reminder to ${email}. In the meantime, feel free to explore around or ask me any questions!`,
        [
          { text: "Actually, let me sign up now", action: 'navigate_signup', icon: <ArrowRight className="w-4 h-4" /> },
          { text: "Thanks, see you later!", action: 'goodbye', icon: <Heart className="w-4 h-4" /> }
        ]
      );
    }, 1000);
  };

  const handleSendMessage = () => {
    if (!input.trim()) return;
    
    // Log session start on first interaction (not on component mount)
    if (!firstInteractionLogged.current) {
      firstInteractionLogged.current = true;
      logConversationData('session_start');
    }
    
    addUserMessage(input);
    const userInput = input.toLowerCase();
    setInput('');

    // Simple NLP for free-form responses
    setTimeout(() => {
      if (userInput.includes('sign up') || userInput.includes('join')) {
        handleOptionClick('signup');
      } else if (userInput.includes('remind') || userInput.includes('later')) {
        handleOptionClick('reminder');
      } else if (userInput.includes('merchant') || userInput.includes('business')) {
        handleOptionClick('merchant_interest');
      } else if (userInput.includes('reward') || userInput.includes('earn')) {
        handleOptionClick('rewards_interest');
      } else if (userInput.includes('bye') || userInput.includes('goodbye') || userInput.includes('thanks') || userInput.includes('thank you') || userInput.includes('see you') || userInput.includes('later') || userInput.includes('cya')) {
        addBotMessage(
          `Thanks for chatting with me! 😊 It was great getting to know you. If you ever change your mind, we'll be here building the payment network of the future. Have a wonderful day! 👋`,
          [
            { text: "Actually, let me sign up now", action: 'navigate_signup', icon: <ArrowRight className="w-4 h-4" /> },
            { text: "Set a reminder for later", action: 'reminder', icon: <Clock className="w-4 h-4" /> }
          ]
        );
      } else {
        addBotMessage(
          `That's a great question! 🤔 I want to make sure I give you the best answer. What aspect of Vaiu interests you most?`,
          [
            { text: "Earning rewards", action: 'rewards_interest', icon: <DollarSign className="w-4 h-4" /> },
            { text: "Finding merchants", action: 'merchant_discovery', icon: <Store className="w-4 h-4" /> },
            { text: "Business opportunities", action: 'merchant_interest', icon: <TrendingUp className="w-4 h-4" /> },
            { text: "Just exploring", action: 'browsing', icon: <Users className="w-4 h-4" /> }
          ]
        );
      }
    }, 1000);
  };

  const renderMessage = (message: Message) => {
    const isBot = message.type === 'bot';
    const isUser = message.type === 'user';

    return (
      <div key={message.id} className={`mb-3 px-2`}>
        <div className={`w-full ${isUser ? 'flex justify-end' : ''}`}>
          {/* Message bubble - with proper width constraints */}
          <div className={`rounded-2xl px-3 py-2 w-full ${
            isBot 
              ? 'bg-white border border-gray-200 shadow-sm' 
              : 'bg-gray-600 text-white'
          }`}>
            <div className="whitespace-pre-wrap text-sm leading-relaxed break-words">
              {message.text}
            </div>
            
            {/* Custom component */}
            {message.component && (
              <div className="mt-3">
                {message.component}
              </div>
            )}
            
            {/* Options */}
            {message.options && (
              <div className="mt-3 space-y-2">
                {message.options.map((option, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleOptionClick(option.action, option.value, option.text)}
                    className="w-full justify-start text-left hover:bg-gray-100 hover:border-gray-300 text-xs sm:text-sm whitespace-normal"
                  >
                    {option.icon && <span className="mr-2 flex-shrink-0">{option.icon}</span>}
                    <span className="break-words">{option.text}</span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // All hooks must be called before any conditional returns (React Rules of Hooks)
  useEffect(() => {
    // Only add greeting if not already initialized (prevents duplication in React strict mode)
    if (!greetingInitialized.current) {
      greetingInitialized.current = true;
      
      // Don't log anything automatically - wait for actual user interaction
      // This prevents unnecessary logging when users are just browsing
      // We track visitor counts with Google Analytics instead
      
      // Start with greeting
      setTimeout(() => {
        addBotMessage(
          "👋 Hey there! Welcome! I'm here to help you discover this tokenized atomic marketplace can be useful to you. What brings you here today?",
          [
            { text: "I'm curious about earning rewards", action: 'rewards_interest', icon: <DollarSign className="w-4 h-4" /> },
            { text: "Want to find new places to shop", action: 'merchant_discovery', icon: <ShoppingBag className="w-4 h-4" /> },
            { text: "I'm a business owner", action: 'merchant_interest', icon: <Store className="w-4 h-4" /> },
            { text: "I don't want to share my phone number just to get paid...", action: 'privacy_motivated', icon: <Shield className="w-4 h-4" /> },
            { text: "I love tech and AI and I want a faster, safer, more equitable way to pay and get paid.", action: 'tech_motivated', icon: <Zap className="w-4 h-4" /> },
            { text: "Just browsing around", action: 'browsing', icon: <Users className="w-4 h-4" /> }
          ]
        );
      }, 500);
    }

    // Don't auto-scroll on initialization to prevent page jumping
    // scrollToBottom();
  }, []); // Back to empty dependency array since we're using ref for tracking

  useEffect(() => {
    // Only scroll when messages are actually added after initial mount
    // Skip scrolling on initial render to prevent page jump
    if (messages.length > 1) {
      scrollToBottom();
    }
  }, [messages]);

  // Check if merchant onboarding is already complete
  let isMerchantOnboarded = false;
  if (user) {
    const userModel = new UserModel(user);
    isMerchantOnboarded = userModel.isMerchantOnboarded;
  }
  
  // Also check localStorage as a fallback (for recently completed onboarding)
  const merchantOnboardingComplete = localStorage.getItem('merchantOnboardingComplete') === 'true';
  
  // This component is only for prospective users and guests
  // Hide for: registered non-guest users OR users who completed merchant onboarding
  // NOTE: This check happens AFTER all hooks to comply with React Rules of Hooks
  if (!authLoading && ((user && !isGuest) || isMerchantOnboarded || merchantOnboardingComplete)) {
    // Registered user or completed merchant - they shouldn't see this component at all
    // Return null to prevent any rendering or side effects
    return null;
  }

  return (
    <Card className="w-full border-0 shadow-lg overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white p-4 sm:p-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Join the Marketplace</h2>
                <p className="text-sm sm:text-base text-gray-200">Let's discover what we can do for you!</p>
              </div>
            </div>
          </div>

          {/* Chat area */}
          <div className="h-96 overflow-y-auto overflow-x-hidden bg-gray-50">
            {messages.map(renderMessage)}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="mb-4 px-2">
                <div className="bg-white rounded-2xl px-3 py-2 border border-gray-200 inline-block">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-2 border-t bg-white">
            <div className="flex space-x-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                className="flex-1 text-sm"
              />
              <Button 
                onClick={handleSendMessage}
                size="sm"
                className="px-3 sm:px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
  );
};

// Stats component for displaying network statistics
const StatsComponent: React.FC<{ stats: any }> = ({ stats }) => (
  <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-4 rounded-lg">
    <div className="grid grid-cols-2 gap-4">
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-800">{stats.merchants.toLocaleString()}</div>
        <div className="text-xs text-gray-600">Merchants</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-800">{stats.advocates.toLocaleString()}</div>
        <div className="text-xs text-gray-600">Advocates</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-800">{stats.requests.toLocaleString()}</div>
        <div className="text-xs text-gray-600">Requests</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-gray-800">${stats.rewards.toLocaleString()}</div>
        <div className="text-xs text-gray-600">Rewards Paid</div>
      </div>
    </div>
  </div>
);

// Email capture component
const EmailCapture: React.FC<{ onSubmit: (email: string) => void }> = ({ onSubmit }) => {
  const [email, setEmail] = useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      onSubmit(email);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex space-x-2">
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        required
        className="flex-1"
      />
      <Button type="submit" size="sm">
        <Send className="w-4 h-4" />
      </Button>
    </form>
  );
};

export default InteractiveOnboarding; 